import type { Id } from "@supermanus/document-model";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { arrayMoveIds } from "./usePointerReorder";

export interface UseSceneReorderOptions {
  readOnly?: boolean;
  /** Move scene so it sits before `beforeSceneId`, or at end when null. */
  onMoveScene: (sceneId: Id, beforeSceneId: Id | null) => void;
}

export interface UseSceneReorderResult {
  listRef: RefObject<HTMLElement | null>;
  dragSceneId: Id | null;
  /** Scene id that the dragged item would insert before; `null` = end; `undefined` = no indicator. */
  dropBeforeSceneId: Id | null | undefined;
  beginSceneDrag: (
    sceneId: Id,
    e: ReactPointerEvent<HTMLElement>,
  ) => void;
}

function collectSceneItems(root: HTMLElement): {
  ids: Id[];
  els: HTMLElement[];
} {
  const ids: Id[] = [];
  const els: HTMLElement[] = [];
  for (const el of root.querySelectorAll<HTMLElement>("[data-scene-id]")) {
    const id = el.dataset.sceneId;
    if (!id) continue;
    ids.push(id);
    els.push(el);
  }
  return { ids, els };
}

/**
 * Map pointer Y to “insert before this id” (null = after last), using the
 * same index math as chip/step reorder so skipping the dragged row does not
 * jump the drop target.
 */
function dropBeforeFromPointer(
  root: HTMLElement,
  dragId: Id,
  clientY: number,
): Id | null {
  const { ids, els } = collectSceneItems(root);
  const from = ids.indexOf(dragId);
  if (from < 0) return null;

  let to = ids.length;
  for (let i = 0; i < els.length; i++) {
    const rect = els[i]!.getBoundingClientRect();
    if (clientY < rect.top) {
      to = i;
      break;
    }
    // Upper half → insert before this row; lower half → after it.
    if (clientY <= rect.top + rect.height / 2) {
      to = i;
      break;
    }
    if (clientY <= rect.bottom) {
      to = i + 1;
      break;
    }
  }
  to = Math.max(0, Math.min(ids.length, to));

  const next = arrayMoveIds(ids, from, to);
  const at = next.indexOf(dragId);
  return next[at + 1] ?? null;
}

/**
 * Pointer-based scene reorder for lists marked with `[data-scene-id]`.
 *
 * Listeners live on `window` (no setPointerCapture). Capturing on the grip
 * used to fire `pointercancel` when React re-rendered drop/drag classes and
 * remounted the button — that committed the move at a mid-drag position.
 */
export function useSceneReorder({
  readOnly,
  onMoveScene,
}: UseSceneReorderOptions): UseSceneReorderResult {
  const listRef = useRef<HTMLElement | null>(null);
  const [dragSceneId, setDragSceneId] = useState<Id | null>(null);
  const [dropBeforeSceneId, setDropBeforeSceneId] = useState<
    Id | null | undefined
  >(undefined);
  const onMoveSceneRef = useRef(onMoveScene);
  const readOnlyRef = useRef(readOnly);
  const endDragRef = useRef<(() => void) | null>(null);
  onMoveSceneRef.current = onMoveScene;
  readOnlyRef.current = readOnly;

  useEffect(() => {
    return () => {
      endDragRef.current?.();
      document.body.classList.remove("sm-is-scene-dragging");
    };
  }, []);

  const beginSceneDrag = useCallback(
    (sceneId: Id, e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      if (readOnlyRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const pointerId = e.pointerId;
      endDragRef.current?.();

      let latestBefore: Id | null = null;
      let moved = false;
      const startY = e.clientY;
      let finished = false;

      const onMovePtr = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        if (!moved && Math.abs(ev.clientY - startY) <= 5) return;
        if (!moved) {
          moved = true;
          document.body.classList.add("sm-is-scene-dragging");
          setDragSceneId(sceneId);
        }
        const root = listRef.current;
        if (!root) return;
        const before = dropBeforeFromPointer(root, sceneId, ev.clientY);
        latestBefore = before;
        setDropBeforeSceneId(before);
      };

      const finish = (commit: boolean) => {
        if (finished) return;
        finished = true;
        window.removeEventListener("pointermove", onMovePtr);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        endDragRef.current = null;
        document.body.classList.remove("sm-is-scene-dragging");
        if (commit && moved) onMoveSceneRef.current(sceneId, latestBefore);
        setDragSceneId(null);
        setDropBeforeSceneId(undefined);
      };

      const onUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        finish(true);
      };
      const onCancel = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        // Do not commit mid-path — cancel means the gesture was interrupted.
        finish(false);
      };

      window.addEventListener("pointermove", onMovePtr);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      endDragRef.current = () => finish(false);
    },
    [],
  );

  return {
    listRef,
    dragSceneId,
    dropBeforeSceneId,
    beginSceneDrag,
  };
}

/** CSS class for a drop line that does not insert a layout-shifting node. */
export function reorderDropClass(
  dragId: Id | null,
  dropBeforeId: Id | null | undefined,
  itemId: Id,
  isLast: boolean,
): string {
  if (dragId == null || dropBeforeId === undefined) return "";
  if (dropBeforeId === itemId && dragId !== itemId) return " is-drop-before";
  if (dropBeforeId === null && isLast) return " is-drop-after";
  return "";
}
